// File: frontend/src/views/private/admin/UMS.js

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import apiService from '../../../services/apiService';
import AdminPageTemplate from '../../../components/layout/AdminPageTemplate';
import { 
  UserStatsCards, 
  UserFilters, 
  UserTable, 
  UserDetailsModal 
} from '../../../components/ums';
import { Users } from 'lucide-react';

const UMS = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [totalCount, setTotalCount] = useState(null); // ✅ ADD THIS LINE
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    limit: 50,
    user_status: '',
    user_role: '',
    is_anonymous: '',
    journey_stage: '',
    email_address: '',
    phone_number: '',
    start_date: '',
    end_date: ''
  });
  
  // Pagination
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  
  // Selected user for details modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Auth check - redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Check for user_id in URL params (from EMS link)
  useEffect(() => {
    const userIdParam = searchParams.get('user_id');
    if (userIdParam && isAuthenticated && user) {
      handleViewDetailsById(userIdParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAuthenticated, user]);

  // Load users and stats on mount
  useEffect(() => {
    if (isAuthenticated && user) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  const loadData = async () => {
    if (!user || !user.user_id) {
      console.error('No user ID available');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Build filter params
      const filterParams = {
        limit: filters.limit
      };

      if (filters.user_status) filterParams.user_status = filters.user_status;
      if (filters.user_role) filterParams.user_role = filters.user_role;
      if (filters.is_anonymous !== '') filterParams.is_anonymous = filters.is_anonymous === 'true';
      if (filters.journey_stage) filterParams.journey_stage = filters.journey_stage;
      if (filters.email_address) filterParams.email_address = filters.email_address;
      if (filters.phone_number) filterParams.phone_number = filters.phone_number;
      if (filters.start_date) filterParams.start_date = filters.start_date;
      if (filters.end_date) filterParams.end_date = filters.end_date;

      // Load users
      const usersResult = await apiService.getUsers(filterParams, user.user_id);
      
      if (usersResult.success) {
        setUsers(usersResult.data.users || []);
        setTotalCount(usersResult.data.pagination?.total_count || null); // ✅ ADD THIS LINE
        setLastEvaluatedKey(usersResult.data.pagination?.last_evaluated_key || null);
        setHasMore(usersResult.data.pagination?.has_more || false);
      } else {
        setError(usersResult.error || 'Failed to load users');
      }
      
      // Load stats
      const statsResult = await apiService.getUserStats(
        { timeframes: ['24h', '7d', '30d'] },
        user.user_id
      );
      
      if (statsResult.success) {
        setStats(statsResult.data.stats || null);
      }
      
    } catch (err) {
      console.error('Error loading UMS data:', err);
      setError(err.message || 'An error occurred while loading data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setLastEvaluatedKey(null);
    setUsers([]); // ✅ ADD THIS LINE - Clear users before new query
    loadData();
  };

  const handleResetFilters = async () => {
    // Reset filters state
    const defaultFilters = {
      limit: 50,
      user_status: '',
      user_role: '',
      is_anonymous: '',
      journey_stage: '',
      email_address: '',
      phone_number: '',
      start_date: '',
      end_date: ''
    };
    
    setFilters(defaultFilters);
    setLastEvaluatedKey(null);
    setUsers([]); // ✅ ADD THIS LINE - Clear users before reset
    
    // Immediately load data with default filters
    if (!user || !user.user_id) {
      console.error('No user ID available');
      return;
    }
  
    setIsLoading(true);
    setError(null);
    
    try {
      // Load users with default filters (just limit)
      const usersResult = await apiService.getUsers(
        { limit: defaultFilters.limit },
        user.user_id
      );
      
      if (usersResult.success) {
        setUsers(usersResult.data.users || []);
        setTotalCount(usersResult.data.pagination?.total_count || null); // ✅ ADD THIS LINE
        setLastEvaluatedKey(usersResult.data.pagination?.last_evaluated_key || null);
        setHasMore(usersResult.data.pagination?.has_more || false);
      } else {
        setError(usersResult.error || 'Failed to load users');
      }
      
      // Load stats
      const statsResult = await apiService.getUserStats(
        { timeframes: ['24h', '7d', '30d'] },
        user.user_id
      );
      
      if (statsResult.success) {
        setStats(statsResult.data.stats || null);
      }
      
    } catch (err) {
      console.error('Error loading UMS data:', err);
      setError(err.message || 'An error occurred while loading data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!hasMore || !lastEvaluatedKey || !user || !user.user_id) return;
    
    setIsLoading(true);
    
    try {
      const filterParams = {
        limit: filters.limit,
        last_evaluated_key: lastEvaluatedKey
      };

      if (filters.user_status) filterParams.user_status = filters.user_status;
      if (filters.user_role) filterParams.user_role = filters.user_role;
      if (filters.is_anonymous !== '') filterParams.is_anonymous = filters.is_anonymous === 'true';
      if (filters.journey_stage) filterParams.journey_stage = filters.journey_stage;
      if (filters.email_address) filterParams.email_address = filters.email_address;
      if (filters.phone_number) filterParams.phone_number = filters.phone_number;
      if (filters.start_date) filterParams.start_date = filters.start_date;
      if (filters.end_date) filterParams.end_date = filters.end_date;

      const result = await apiService.getUsers(filterParams, user.user_id);
      
      if (result.success) {
        setUsers(prev => [...prev, ...(result.data.users || [])]); // ✅ This line already correct (accumulates)
        setLastEvaluatedKey(result.data.pagination?.last_evaluated_key || null);
        setHasMore(result.data.pagination?.has_more || false);
      }
    } catch (err) {
      console.error('Error loading more users:', err);
      setError(err.message || 'Failed to load more users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = async (selectedUser) => {
    if (!user || !user.user_id) return;

    setSelectedUser(selectedUser);
    setLoadingDetails(true);
    setUserDetails(null);
    
    try {
      const result = await apiService.getUserDetails(
        { user_id: selectedUser.user_id },
        user.user_id
      );
      
      if (result.success) {
        setUserDetails(result.data);
      } else {
        setError(result.error || 'Failed to load user details');
      }
    } catch (err) {
      console.error('Error loading user details:', err);
      setError(err.message || 'Failed to load user details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewDetailsById = async (userId) => {
    if (!user || !user.user_id) return;

    setSelectedUser({ user_id: userId });
    setLoadingDetails(true);
    setUserDetails(null);
    
    try {
      const result = await apiService.getUserDetails(
        { user_id: userId },
        user.user_id
      );
      
      if (result.success) {
        setUserDetails(result.data);
      } else {
        setError(result.error || 'Failed to load user details');
      }
    } catch (err) {
      console.error('Error loading user details:', err);
      setError(err.message || 'Failed to load user details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDetails = () => {
    setSelectedUser(null);
    setUserDetails(null);
    // Clear user_id from URL if present
    if (searchParams.get('user_id')) {
      navigate('/admin/ums', { replace: true });
    }
  };

  // Don't render anything while checking auth
  if (authLoading) {
    return null;
  }

  // Don't render if not authenticated (will redirect via useEffect)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <AdminPageTemplate
      title="User Management System"
      subtitle="Manage users, roles, and permissions across the platform"
      icon={Users}
    >
      {/* Stats Cards */}
      <UserStatsCards stats={stats} isLoading={isLoading && !stats} />

      {/* Filters */}
      <UserFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        isLoading={isLoading}
      />

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '16px',
          marginBottom: '20px',
          background: 'var(--color-error-bg)',
          border: '1px solid var(--color-error-border)',
          borderRadius: 'var(--border-radius-lg)',
          color: 'var(--color-error)',
          fontSize: 'var(--font-size-sm)'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Users Table */}
      <UserTable
        users={users}
        totalCount={totalCount} // ✅ ADD THIS LINE
        isLoading={isLoading}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        onViewDetails={handleViewDetails}
      />

      {/* User Details Modal */}
      <UserDetailsModal
        isOpen={!!selectedUser}
        onClose={handleCloseDetails}
        userDetails={userDetails}
        isLoading={loadingDetails}
      />
    </AdminPageTemplate>
  );
};

export default UMS;